import { useEffect } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Button, Drawer, Form, Input, InputNumber, Select, message } from 'antd';
import { RS_EDUCATION_LEVEL_OPTIONS } from '@/shared/employee/employee-forms-options';
import {
  createJobPosition,
  updateJobPosition,
} from '@/services/jobPositions.service';
import { useJobPositionsT } from './useJobPositions';

const errorOf = (error) =>
  error?.response?.data?.message || error?.message || '';

/**
 * Add / edit one position of the company's catalog.
 *
 * @param {Object} props
 * @param {string} props.companyId
 * @param {Object|null} props.position - Position being edited, `{}` for a new one, null when closed.
 * @param {Function} props.onClose
 * @param {Function} props.onSaved
 */
export default function JobPositionDrawer({ companyId, position, onClose, onSaved }) {
  const { t, tj } = useJobPositionsT();
  const [form] = Form.useForm();
  const open = !!position;
  const isNew = open && !position._id;
  const level = Form.useWatch('educationLevel', form);

  useEffect(() => {
    if (!open) return;
    form.resetFields();
    form.setFieldsValue({
      nameSr: '',
      nameEn: '',
      educationLevel: undefined,
      educationLevelOther: '',
      description: '',
      sectorName: '',
      headcount: null,
      supervisorFunction: '',
      abCode: '',
      ...position,
    });
  }, [open, position, form]);

  const mutation = useMutation({
    mutationFn: (values) =>
      isNew
        ? createJobPosition({ ...values, company: companyId })
        : updateJobPosition(position._id, values),
    onSuccess: (saved) => {
      message.success(
        saved?.employeesUpdated
          ? tj('savedWithEmployees', { count: saved.employeesUpdated })
          : tj('saved')
      );
      onSaved?.(saved);
      onClose();
    },
    onError: (error) => message.error(errorOf(error) || tj('saveFailed')),
  });

  const levelOptions = RS_EDUCATION_LEVEL_OPTIONS.map(({ key, value }) => ({
    value,
    label: t(`educationLevels.${key}`, { ns: 'forms' }),
  }));

  return (
    <Drawer
      open={open}
      onClose={onClose}
      width={620}
      destroyOnClose
      title={isNew ? tj('addTitle') : tj('editTitle')}
      extra={
        <Button
          type="primary"
          className="!rounded-[50px]"
          loading={mutation.isPending}
          onClick={() => form.submit()}
        >
          {t('save', { ns: 'forms' })}
        </Button>
      }
    >
      <Form form={form} layout="vertical" onFinish={(values) => mutation.mutate(values)}>
        <Form.Item
          name="nameSr"
          label={tj('nameSr')}
          rules={[{ required: true, whitespace: true, message: tj('nameRequired') }]}
        >
          <Input placeholder={tj('nameSr')} />
        </Form.Item>
        <Form.Item name="nameEn" label={tj('nameEn')}>
          <Input placeholder={tj('nameEn')} />
        </Form.Item>
        <div className="grid grid-cols-2 gap-x-3">
          <Form.Item
            name="educationLevel"
            label={tj('educationLevel')}
            rules={[{ required: true, message: tj('levelRequired') }]}
          >
            <Select options={levelOptions} placeholder={tj('educationLevel')} />
          </Form.Item>
          {level === 'other' ? (
            <Form.Item
              name="educationLevelOther"
              label={tj('educationLevelOther')}
              extra={tj('educationLevelOtherHint')}
              rules={[{ required: true, whitespace: true, message: tj('levelRequired') }]}
            >
              <Input placeholder="IV/V" />
            </Form.Item>
          ) : (
            <div />
          )}
        </div>
        <Form.Item name="description" label={tj('description')}>
          <Input.TextArea autoSize={{ minRows: 6, maxRows: 18 }} placeholder={tj('description')} />
        </Form.Item>
        <Form.Item name="sectorName" label={tj('sectorName')} extra={tj('sectorNameHint')}>
          <Input placeholder={tj('sectorName')} />
        </Form.Item>
        <div className="grid grid-cols-2 gap-x-3">
          <Form.Item name="headcount" label={tj('headcount')}>
            <InputNumber min={0} precision={0} className="!w-full" />
          </Form.Item>
          <Form.Item name="abCode" label={tj('abCode')} extra={tj('abCodeHint')}>
            <Input placeholder={tj('abCode')} />
          </Form.Item>
        </div>
        <Form.Item name="supervisorFunction" label={tj('supervisorFunction')}>
          <Input placeholder={tj('supervisorFunction')} />
        </Form.Item>
      </Form>
    </Drawer>
  );
}
